import { OrderDocument, OrderItemDocument } from '@models/order';
import { UserDocument } from '@models/user';
import { db, FirebaseCallable, functions, httpsCallable } from '@util/firebase';
import { CarrierCode } from '@util/maps/carriers';
import {
  collection,
  CollectionReference,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { AddressDocument } from 'models/address';
import { ProductDocument } from 'models/product';
import { Rate, getRates } from '../shipengine';
import { createPaymentIntentForLabel } from '../stripe';
import { LabelDocument } from './labels.types';
import { updateSellers } from '../orders';

const labelsRef = collection(
  db,
  'labels'
) as CollectionReference<LabelDocument>;

export async function getLabels(uid: string) {
  if (!uid) return [];
  const q = query(
    labelsRef,
    where('uid', '==', uid),
    orderBy('created', 'desc')
    // LIMIT 10
  );
  const snapshot = await getDocs(q);
  const docs = snapshot.docs.map((d) => d.data()) as LabelDocument[];
  return docs;
}

export async function getPaymentIntentForLabel({
  selectedRate,
  userDoc,
  payment_method,
  order,
}: {
  selectedRate: Rate;
  userDoc: UserDocument;
  payment_method: string;
  order?: OrderDocument | null;
}) {
  const { data } = await createPaymentIntentForLabel({
    amount: selectedRate?.total_amount ?? 0,
    customer_id: userDoc?.customer_id ?? '',
    metadata: {
      is_label: 'true',
      order_id: order ? order.id : 'No order',
      rate_id: selectedRate?.rate_id ?? '',
    },
    payment_method,
  });
  return data;
}

export async function getLabelById(id: string) {
  const snapshot = await getDocs(query(labelsRef, where('label_id', '==', id)));
  const docs = snapshot.docs
    .map((d) => d.data())
    .filter(Boolean) as LabelDocument[];
  return docs[0];
}

interface LabelHelperRequest {
  uid: string;
  rate_id: string;
  address: AddressDocument;
  email?: string;
  buyer_id?: string;
  first?: string;
  order_id?: string;
  order_num?: number;
  insurance_amount?: number;
  retry?: boolean;
  pi_id?: string;
  product?: ProductDocument;
}

interface CreateAndChargeLabelArgs extends LabelHelperRequest {
  amount: number;
  customer_id: string;
  payment_method: string;
  items?: OrderItemDocument[]; // if printing for an order
}

export interface LabelResponse {
  label_id: string;
  status: string;
  shipment_id: string;
  ship_date: string;
  created_at: string;
  shipment_cost: {
    currency: string;
    amount: number;
  };
  insurance_cost: {
    currency: string;
    amount: number;
  };
  tracking_number: string;
  is_return_label: boolean;
  rma_number: null;
  is_international: boolean;
  batch_id: string;
  carrier_id: string;
  service_code: string;
  package_code: string;
  voided: boolean;
  voided_at: null;
  label_format: string;
  display_scheme: string;
  label_layout: string;
  trackable: boolean;
  label_image_id: null;
  carrier_code: CarrierCode;
  tracking_status: string;
  label_download: {
    pdf: string;
    png: string;
    zpl: string;
    href: string;
  };
  form_download: { href: string } | null;
  insurance_claim: null;
  packages: {
    package_id: number;
    package_code: string;
    weight: {
      value: number;
      unit: string;
    };
    dimensions: {
      unit: string;
      length: number;
      width: number;
      height: number;
    };
    insured_value: {
      currency: string;
      amount: number;
    };
    tracking_number: string;
    label_messages: {
      reference1: string;
      reference2: string;
      reference3: string;
    };
    external_package_id: string;
    sequence: number;
  }[];
  charge_event: string;
}

interface LabelHelperResponse extends LabelResponse {
  rate_id: string;
  charged: number;
  seller_id: string;
  label_doc_id: string;
}

export async function createAndChargeLabel(args: CreateAndChargeLabelArgs) {
  return await httpsCallable<
    CreateAndChargeLabelArgs,
    {
      success: boolean;
      pi_id: string;
      label?: LabelHelperResponse;
      error?: string;
    }
  >(
    functions,
    FirebaseCallable.createAndChargeLabel
  )(args);
}

export const fixRateID = async (
  product: ProductDocument,
  order: OrderDocument,
  item: OrderItemDocument,
  seller: string
): Promise<{ oldRateID: string | null; newRateID: string | null } | null> => {
  const oldRateID = item.rate_id || null;
  const rawRates = await getRates({
    product_id: product.id,
    shipment: product.shipment,
    ship_to: order.address,
    offer_price: item.product_cost,
    ...(product.shipment?.ship_from.country_code !==
      order.address.country_code && {
      customs_info: {
        product_title: product.title,
        product_category: product.category,
        product_sku: product.sku ?? '',
        product_price: product.price,
      },
    }),
  });
  const filter = prompt(
    `${item.product_name}: Enter specific carrier code (fedex, ups, usps) or service code (ups_ground, fedex_international_priority, usps_priority) Leave blank to use all carriers`
  );
  const rates = rawRates.filter(
    (r) =>
      r.carrier_code.toLowerCase().includes(filter?.toLowerCase() ?? '') ||
      r.service_code.toLowerCase().includes(filter?.toLowerCase() ?? '')
  );
  // find the rate that matches the buyer shipping cost within $10
  const rate = rates.find(
    (r) => Math.abs(r.total_amount - item.buyer_shipping_cost) < 10
  );
  if (rate) {
    const index = order.sellers![seller].findIndex((i) => i.id === item.id);
    const sellers = order.sellers;
    if (!sellers) return null;
    sellers[item.seller_id!][index].rate_id = rate.rate_id;
    await updateSellers({
      ...order,
      sellers: sellers,
    });
    return { oldRateID, newRateID: rate.rate_id };
  } else {
    // find closest rate
    const closestRate = rates.reduce((prev, curr) =>
      Math.abs(curr.total_amount - item.buyer_shipping_cost) <
      Math.abs(prev.total_amount - item.buyer_shipping_cost)
        ? curr
        : prev
    );
    if (closestRate) {
      const confirm = window.confirm(
        `${
          item.product_name
        }: No exact match found. Closest rate found is ${closestRate.total_amount.toFixed(
          2
        )}. This is ${(
          closestRate.total_amount - item.buyer_shipping_cost
        ).toFixed(
          2
        )} more from what the buyer alread paid. Would you like to use this rate?`
      );
      if (confirm) {
        const index = order.sellers![seller].findIndex((i) => i.id === item.id);
        const sellers = order.sellers;
        if (!sellers) return null;
        sellers[item.seller_id!][index].rate_id = closestRate.rate_id;
        await updateSellers({
          ...order,
          sellers: sellers,
        });
        return { oldRateID, newRateID: closestRate.rate_id };
      }
    }
  }
  return null;
};
